/**
 * Photo list for a Facebook PHOTO post (2026-10-14).
 *
 * A Photo post can carry up to 10 photos. The editor picks them from
 * the article's photos (featured image, carousel library, source
 * photos) or adds a new one, and orders them; the first is the big one
 * in Facebook's grid and the only one that gets the sponsor ad. The
 * backend gets the storage paths in that order (`image_paths`, see
 * backend/fb_photos.py). Link posts never use this.
 *
 * Each entry is an option from carouselLayout.imageOptions():
 *   { url, label, credit, importFrom? }
 * `importFrom` marks a source photo not yet copied into our storage —
 * it has to be imported before it can be posted (the panel does that
 * when it's added, so the list only ever holds stored photos).
 *
 * Pure and framework-free — node --test frontend/scripts/fbPhotos.test.mjs
 */

export const MAX_FB_PHOTOS = 10;

const FILES = "/api/files/";

/** "/api/files/<path>" (relative or absolute) → "<path>"; "" otherwise. */
export function storagePathFromUrl(url) {
  if (!url) return "";
  const i = url.indexOf(FILES);
  if (i < 0) return "";
  return url.slice(i + FILES.length);
}

/** The starting list: the featured image when the article has one. */
export function initialPhotos(article) {
  const url = article?.generated_image_url || "";
  return storagePathFromUrl(url) ? [{ url, label: "Featured image" }] : [];
}

const key = (p) => storagePathFromUrl(p?.url) || p?.url || "";

export function hasPhoto(list, url) {
  const k = key({ url });
  return (list || []).some((p) => key(p) === k);
}

/** Append a stored photo (no duplicates, never past the cap). */
export function addPhoto(list, photo) {
  const cur = list || [];
  if (!photo?.url || !storagePathFromUrl(photo.url)) return cur;
  if (hasPhoto(cur, photo.url) || cur.length >= MAX_FB_PHOTOS) return cur;
  return [...cur, { url: photo.url, label: photo.label || "Photo" }];
}

export function removePhoto(list, index) {
  return (list || []).filter((_, i) => i !== index);
}

/** Move the photo at `from` to position `to` (clamped). */
export function movePhoto(list, from, to) {
  const cur = [...(list || [])];
  if (from < 0 || from >= cur.length) return cur;
  const dest = Math.max(0, Math.min(cur.length - 1, to));
  if (dest === from) return cur;
  const [item] = cur.splice(from, 1);
  cur.splice(dest, 0, item);
  return cur;
}

/** Storage paths in post order — what the share request sends. */
export function photoPaths(list) {
  return (list || []).map((p) => storagePathFromUrl(p.url)).filter(Boolean);
}

/** Article photos not already in the post. A source photo whose stored
 *  copy is already in the post counts as in it (same url). */
export function pickablePhotos(options, list) {
  return (options || []).filter((o) => o?.url && !hasPhoto(list, o.url));
}

/** Short label for the section header. */
export function photoCountLabel(n) {
  if (!n) return "No photos yet";
  return n === 1 ? "1 photo" : `${n} photos`;
}
